import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../components/common/layout/Header";
import Footer from "../../components/common/layout/Footer";
import { getSession } from "@/utils/session";
import { searchDomains, DomainSearchResult, getAvailableTlds, getCustomerDetails } from "@/services/dreamscapeApi";
import { addDomainToCart } from "@/utils/cart";
import { GradientButton } from "../../components/common/ui/Button";
import { AnimatedButton } from "../../components/common/animations";
import { CheckCircle2, ChevronDown, Search, ShoppingCart, Sparkles, Loader2 } from "lucide-react";
import {
    CurrencyCode,
    currencies,
    convertCurrency,
    formatPriceWithCurrency
} from "@/utils/currencyConverter";

export default function DomainSearchResults() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const searchQuery = searchParams.get("q") || "";

    const [searchInput, setSearchInput] = useState(searchQuery);
    const [exactResult, setExactResult] = useState<DomainSearchResult | null>(null);
    const [suggestedResults, setSuggestedResults] = useState<DomainSearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedCurrency, setSelectedCurrencyState] = useState<CurrencyCode>(
        (localStorage.getItem('preferredCurrency') as CurrencyCode) || 'AUD'
    );

    const setSelectedCurrency = (curr: CurrencyCode) => {
        setSelectedCurrencyState(curr);
        localStorage.setItem('preferredCurrency', curr);
    };
    const [popularTldsPrices, setPopularTldsPrices] = useState<Record<string, number>>({});
    const [tldCatalog, setTldCatalog] = useState<Record<string, number>>({});
    const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const popularTlds = [
        { tld: "com.au" },
        { tld: "info" },
        { tld: "net" },
        { tld: "au" },
        { tld: "org" },
        { tld: "co" },
    ];

    const TLD_BATCHES = [
        ['com', 'net', 'org', 'io', 'co', 'info', 'biz', 'xyz', 'app', 'dev', 'tech', 'online', 'au', 'com.au', 'net.au', 'org.au', 'co.uk', 'sydney', 'melbourne'],
        ['me', 'pw', 'icu', 'top', 'vip', 'shop', 'store', 'blog', 'news', 'site', 'website', 'cloud', 'digital', 'host', 'agency', 'global', 'link', 'live', 'one', 'page'],
        ['solutions', 'services', 'company', 'systems', 'network', 'group', 'pro', 'center', 'world', 'life', 'design', 'expert', 'media', 'press', 'works', 'space', 'zone', 'team', 'today', 'run'],
        ['it', 'fr', 'de', 'jp', 'ca', 'us', 'eu', 'asia', 'club', 'pro', 'mobi', 'travel', 'jobs', 'museum', 'tel', 'name', 'co.nz', 'co.za', 'co.in', 'co.jp']
    ];

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setCurrencyDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (searchQuery) performSearch(searchQuery);
    }, [searchQuery]);

    useEffect(() => {
        const fetchCatalog = async () => {
            try {
                // Sync currency if logged in but preference is missing
                const hasManualPref = localStorage.getItem('preferredCurrency');
                if (!hasManualPref) {
                    const session = getSession();
                    if (session) {
                        try {
                            const customerRes = await getCustomerDetails(session.customerId);
                            if (customerRes.success && customerRes.customer) {
                                const country = (customerRes.customer.country || customerRes.customer.country_code || '').toUpperCase();
                                let prefCurrency: CurrencyCode = 'AUD';
                                if (country === 'NP' || country === 'NEPAL') prefCurrency = 'NRS';
                                else if (country === 'AU' || country === 'AUSTRALIA') prefCurrency = 'AUD';
                                setSelectedCurrency(prefCurrency);
                            }
                        } catch (e) {
                            console.warn("DomainSearchResults currency sync error:", e);
                        }
                    }
                }

                const response = await getAvailableTlds({ currency: 'USD' }); // Get base prices in USD
                if (response.success && response.tlds) {
                    const catalog: Record<string, number> = {};
                    response.tlds.forEach((t: any) => {
                        let tld = (t.tld || '').toLowerCase();
                        if (tld.startsWith('.')) tld = tld.substring(1);
                        const price = t.registerPrice || t.price?.register || t.register_price || t.price || 0;
                        if (price > 0 && tld) catalog[tld] = price;
                    });
                    setTldCatalog(catalog);
                    const initialPopularPrices: Record<string, number> = {};
                    popularTlds.forEach(({ tld }) => {
                        const tldKey = tld.toLowerCase().startsWith('.') ? tld.toLowerCase().substring(1) : tld.toLowerCase();
                        initialPopularPrices[tld] = catalog[tldKey] || 0;
                    });
                    setPopularTldsPrices(initialPopularPrices);
                }
            } catch (err) {
                console.error("Failed to fetch TLD catalog:", err);
            }
        };
        fetchCatalog();
    }, []);

    const performSearch = async (query: string, isLoadMore: boolean = false) => {
        if (!query.trim()) return;
        if (isLoadMore) { setIsLoadingMore(true); } else { setLoading(true); setCurrentBatchIndex(0); }
        setError(null);
        try {
            const trimmed = query.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
            const parts = trimmed.split('.');
            const base = parts[0];
            if (!base || base.length < 2) {
                setError("Please enter a valid domain name");
                setLoading(false); setIsLoadingMore(false);
                return;
            }
            const batchIndex = isLoadMore ? currentBatchIndex + 1 : 0;
            const tldsToSearch = TLD_BATCHES[batchIndex] || [];
            if (tldsToSearch.length === 0 && isLoadMore) { setIsLoadingMore(false); return; }
            const domainsToSearch = isLoadMore
                ? tldsToSearch.map(tld => `${base}.${tld}`)
                : [trimmed, ...tldsToSearch.map(tld => `${base}.${tld}`)];
            const response = await searchDomains(base, [], domainsToSearch);
            if (response.success && response.results) {
                if (!isLoadMore) {
                    const exact = response.results.find(r => r.domain.toLowerCase() === trimmed.toLowerCase());
                    setExactResult(exact || null);
                    setSuggestedResults(response.results.filter(r => r.domain.toLowerCase() !== trimmed.toLowerCase()).slice(0, 6));
                    setCurrentBatchIndex(0);
                } else {
                    setSuggestedResults(prev => [...prev, ...response.results.filter(r => r.domain.toLowerCase() !== trimmed.toLowerCase())]);
                    setCurrentBatchIndex(batchIndex);
                }
                if (!isLoadMore) {
                    const tldPrices: Record<string, number> = { ...popularTldsPrices };
                    popularTlds.forEach(({ tld }) => {
                        const tldLower = tld.toLowerCase().startsWith('.') ? tld.toLowerCase().substring(1) : tld.toLowerCase();
                        const domainResult = response.results.find(r => {
                            const dl = r.domain.toLowerCase();
                            return dl === `${base}.${tldLower}` || dl.endsWith(`.${tldLower}`);
                        });
                        if (domainResult?.registerPrice) tldPrices[tld] = domainResult.registerPrice;
                        else if (tldCatalog[tldLower]) tldPrices[tld] = tldCatalog[tldLower];
                    });
                    setPopularTldsPrices(tldPrices);
                }
            } else {
                if (!isLoadMore) setError(response.message || "Failed to search domains");
            }
        } catch (err) {
            if (!isLoadMore) setError(err instanceof Error ? err.message : "An error occurred");
        } finally {
            setLoading(false); setIsLoadingMore(false);
        }
    };

    const handleSearch = (query: string) => {
        if (query.trim()) {
            if (import.meta.env.VITE_USE_DB === 'false') {
                const storefrontUrl = import.meta.env.VITE_DREAMSCAPE_STOREFRONT_URL || 'https://sandomains.com';
                const searchUrl = `${storefrontUrl.replace(/\/$/, '')}/domain-names/search/?domain=${encodeURIComponent(query.trim())}`;
                window.open(searchUrl, '_blank');
            } else {
                navigate(`/domains/search?q=${encodeURIComponent(query.trim())}`);
            }
        }
    };

    const handleAddToCart = (domain: string, price: number, period: number = 1) => {
        if (import.meta.env.VITE_USE_DB === 'false') {
            const storefrontUrl = import.meta.env.VITE_DREAMSCAPE_STOREFRONT_URL || 'https://sandomains.com';
            const searchUrl = `${storefrontUrl.replace(/\/$/, '')}/domain-names/search/?domain=${encodeURIComponent(domain)}`;
            window.open(searchUrl, '_blank');
            return;
        }

        let finalPeriod = period;

        // .com.au Offer: we default to 3 years to apply the discount
        if (domain.toLowerCase().endsWith('.com.au')) {
            finalPeriod = 3;
        }

        addDomainToCart({
            name: domain,
            price: price,
            currency: selectedCurrency,
            period: finalPeriod,
            privacy: true,
            available: true,
        });
        // The CartSidebar component handles showing the cart and checkout redirection now
    };

    const formatPrice = (price: number | null | undefined, domainName?: string, isOfferDisplay?: boolean): string => {
        let finalPrice = price;
        if (!finalPrice || finalPrice <= 0) {
            if (domainName) {
                const parts = domainName.split('.');
                if (parts.length > 1) finalPrice = tldCatalog[parts.slice(1).join('.').toLowerCase()];
            }
        }
        if (!finalPrice || finalPrice <= 0) return "Price N/A";

        const formattedNormalPrice = formatPriceWithCurrency(convertCurrency(finalPrice, selectedCurrency), selectedCurrency);

        if (isOfferDisplay && domainName?.toLowerCase().endsWith('.com.au')) {
            const offerFirstYear = formatPriceWithCurrency(convertCurrency(0.05, 'USD'), selectedCurrency);
            return `${offerFirstYear} 1st yr, then ${formattedNormalPrice}`;
        }

        return formattedNormalPrice;
    };

    return (
        <div className="min-h-screen text-white">
            {/* Ambient background */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
                <div style={{ position: 'absolute', top: '-20%', left: '60%', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(120,60,255,0.12) 0%, transparent 70%)', borderRadius: '50%' }} />
                <div style={{ position: 'absolute', bottom: '10%', left: '-10%', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(60,120,255,0.08) 0%, transparent 70%)', borderRadius: '50%' }} />
            </div>

            <div style={{ position: 'relative', zIndex: 1 }}>
                <Header />

                {/* Hero / Search */}
                <section style={{ paddingTop: '120px', paddingBottom: '48px' }} className="container">
                    <div style={{ maxWidth: '720px', margin: '0 auto' }}>
                        <p style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', background: 'linear-gradient(135deg, #a878ff 0%, #6890ff 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '16px', textAlign: 'center' }}>
                            Domain Search
                        </p>
                        <h1 style={{ fontSize: 'clamp(2rem, 5vw, 4rem)', fontWeight: 700, textAlign: 'center', marginBottom: '40px', lineHeight: 1.15, color: '#fff', letterSpacing: '-0.02em' }}>
                            Find your perfect{' '}
                            <span style={{ background: 'linear-gradient(135deg, #a878ff 0%, #6890ff 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>domain</span>
                        </h1>

                        {/* Search Bar */}
                        <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', padding: '6px', backdropFilter: 'blur(20px)', boxShadow: '0 4px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)' }}>
                            <div style={{ position: 'relative', flex: 1 }}>
                                <Search style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '18px', height: '18px', color: 'rgba(255,255,255,0.3)' }} />
                                <input
                                    type="text" value={searchInput}
                                    onChange={e => setSearchInput(e.target.value)}
                                    onKeyPress={e => e.key === 'Enter' && handleSearch(searchInput)}
                                    placeholder="Search for a domain..."
                                    style={{ width: '100%', padding: '14px 16px 14px 46px', background: 'transparent', border: 'none', color: '#fff', fontSize: '15px', outline: 'none' }}
                                />
                            </div>
                            <AnimatedButton
                                onClick={() => alert("Try our AI-powered domain tool!")}
                                variant="secondary"
                                className="px-7 py-3 rounded-xl gap-2 font-semibold text-sm"
                            >
                                <Sparkles style={{ width: '14px', height: '14px', color: '#6890ff' }} />
                                AI Suggestions
                            </AnimatedButton>
                        </div>
                    </div>
                </section>

                {/* Loading */}
                {loading && (
                    <section className="container" style={{ padding: '48px 0' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '60px 0' }}>
                            <div style={{ position: 'relative', width: '56px', height: '56px' }}>
                                <div style={{ position: 'absolute', inset: 0, border: '2px solid rgba(104,130,255,0.2)', borderTopColor: '#6890ff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                            </div>
                            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', letterSpacing: '1px' }}>Searching domains...</p>
                        </div>
                        <div style={{ marginTop: '32px' }}>
                            <div style={{ height: '24px', width: '160px', background: 'rgba(255,255,255,0.06)', borderRadius: '8px', marginBottom: '24px', animation: 'pulse 1.5s ease infinite' }} />
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '20px', animation: 'pulse 1.5s ease infinite', animationDelay: `${i * 100}ms` }}>
                                        <div style={{ height: '12px', width: '60px', background: 'rgba(255,255,255,0.08)', borderRadius: '4px', marginBottom: '12px' }} />
                                        <div style={{ height: '20px', width: '80px', background: 'rgba(255,255,255,0.08)', borderRadius: '4px', marginBottom: '8px' }} />
                                        <div style={{ height: '32px', background: 'rgba(255,255,255,0.06)', borderRadius: '8px', marginTop: '16px' }} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Error */}
                {error && !loading && (
                    <div className="container" style={{ padding: '24px 0' }}>
                        <div style={{ background: 'rgba(255,60,60,0.08)', border: '1px solid rgba(255,60,60,0.25)', borderRadius: '12px', padding: '16px', color: '#ff8080', textAlign: 'center', fontSize: '14px' }}>
                            {error}
                        </div>
                    </div>
                )}

                {/* Exact Domain Result */}
                {!loading && !error && exactResult && exactResult.available && (
                    <section className="container" style={{ padding: '0 0 32px' }}>
                        <div className="bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300"
                            style={{ borderRadius: '14px', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', backdropFilter: 'blur(12px)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(40,200,100,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <CheckCircle2 style={{ width: '18px', height: '18px', color: '#3dcc7a' }} />
                                </div>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
                                        <span style={{ fontWeight: 700, fontSize: '18px', color: '#fff' }}>{exactResult.domain}</span>
                                        <span style={{ fontSize: '12px', fontWeight: 600, padding: '2px 10px', borderRadius: '20px', background: 'rgba(40,200,100,0.15)', color: '#3dcc7a', letterSpacing: '0.5px' }}>AVAILABLE</span>
                                    </div>
                                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginTop: '2px' }}>
                                        {exactResult.domain.toLowerCase().endsWith('.com.au')
                                            ? <>3 years — {formatPrice((exactResult.registerPrice || 12.59) < 1 ? 12.59 : exactResult.registerPrice, exactResult.domain, true)}</>
                                            : formatPrice(exactResult.registerPrice, exactResult.domain) !== "Price N/A"
                                                ? <>1 year — {formatPrice(exactResult.registerPrice, exactResult.domain)}</>
                                                : formatPrice(exactResult.registerPrice, exactResult.domain)}
                                    </p>
                                </div>
                            </div>
                            <GradientButton
                                variant="dashboard"
                                onClick={() => {
                                    const isOffer = exactResult.domain.toLowerCase().endsWith('.com.au');
                                    let price = exactResult.registerPrice || 12.59;
                                    if (isOffer && price < 1) price = 12.59; // Force fix if API returns 0.05
                                    const convertedPrice = convertCurrency(price, selectedCurrency);
                                    handleAddToCart(exactResult.domain, convertedPrice, isOffer ? 3 : 1);
                                }}
                                className="flex items-center gap-2 px-6 py-2.5 shadow-xl font-bold flex-shrink-0"
                            >
                                <ShoppingCart style={{ width: '14px', height: '14px' }} />
                                Add to Cart
                            </GradientButton>
                        </div>
                    </section>
                )}

                {/* Most Popular Section */}
                {!loading && searchQuery && (
                    <section className="container" style={{ padding: '16px 0 48px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Sparkles style={{ width: '16px', height: '16px', color: '#7890ff' }} />
                                <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>Most Popular</h2>
                            </div>

                            {/* Custom Currency Dropdown */}
                            <div ref={dropdownRef} style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setCurrencyDropdownOpen(o => !o)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 14px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${currencyDropdownOpen ? 'rgba(104,144,255,0.5)' : 'rgba(255,255,255,0.12)'}`, borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', outline: 'none', minWidth: '155px', justifyContent: 'space-between' }}
                                >
                                    <span>{currencies.find(c => c.code === selectedCurrency)?.name || selectedCurrency}</span>
                                    <ChevronDown style={{ width: '14px', height: '14px', color: '', flexShrink: 0, transform: currencyDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                                </button>

                                {currencyDropdownOpen && (
                                    <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, minWidth: '190px', zIndex: 200, background: '#0d0d12', border: '1px solid rgba(104,120,255,0.25)', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.03)' }}>
                                        {/* Dropdown header */}
                                        <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                            <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(120,144,255,0.7)' }}>Select Currency</span>
                                        </div>
                                        <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                                            {currencies.map((currency, i) => {
                                                const isSelected = selectedCurrency === currency.code;
                                                return (
                                                    <button
                                                        key={currency.code}
                                                        onClick={() => { setSelectedCurrency(currency.code as CurrencyCode); setCurrencyDropdownOpen(false); }}
                                                        style={{ width: '100%', textAlign: 'left', padding: '10px 14px', background: isSelected ? 'rgba(104,120,255,0.18)' : 'transparent', border: 'none', borderBottom: i < currencies.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none', color: isSelected ? '#a0c4ff' : 'rgba(255,255,255,0.75)', fontSize: '13px', fontWeight: isSelected ? 700 : 400, cursor: 'pointer', transition: 'background 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}
                                                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                                                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                                                    >
                                                        <span>{currency.name}</span>
                                                        <span style={{ fontSize: '11px', fontWeight: 600, color: isSelected ? 'rgba(160,196,255,0.7)' : 'rgba(255,255,255,0.3)', letterSpacing: '0.5px' }}>{currency.code}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* TLD Cards */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                            {popularTlds.map((item, index) => {
                                const price = popularTldsPrices[item.tld] || 12;
                                return (
                                    <div
                                        key={index}
                                        className="bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300"
                                        style={{ borderRadius: '14px', padding: '20px 16px 16px', position: 'relative', cursor: 'default' }}
                                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
                                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                                    >
                                        {/* "New" or "Offer" badge */}
                                        <span style={{
                                            position: 'absolute',
                                            top: '10px',
                                            right: '10px',
                                            background: item.tld === 'com.au' ? 'linear-gradient(135deg, #ff6b6b, #ff4d4d)' : 'linear-gradient(135deg, #9060f0, #6080ff)',
                                            color: '#fff',
                                            fontSize: '9px',
                                            fontWeight: 700,
                                            padding: '3px 8px',
                                            borderRadius: '20px',
                                            letterSpacing: '0.5px',
                                            textTransform: 'uppercase',
                                            boxShadow: item.tld === 'com.au' ? '0 0 10px rgba(255,107,107,0.4)' : 'none'
                                        }}>
                                            {item.tld === 'com.au' ? 'OFFER' : 'New'}
                                        </span>
                                        <div style={{ fontSize: '26px', fontWeight: 700, color: '#fff', marginBottom: '6px', letterSpacing: '-0.02em' }}>.{item.tld}</div>
                                        <div style={{ fontSize: '17px', fontWeight: 600, color: '#fff', marginBottom: '16px' }}>
                                            {item.tld === 'com.au' ? formatPrice(price < 1 ? 12.59 : price, `${searchQuery.split('.')[0] || 'test'}.com.au`, true) : (price > 0 ? formatPriceWithCurrency(convertCurrency(price, selectedCurrency), selectedCurrency) : "Price N/A")}
                                            {price > 0 && <span style={{ fontSize: '11px', opacity: 0.7, marginLeft: '2px' }}>/{item.tld === 'com.au' ? '3yrs' : 'yr'}</span>}
                                        </div>
                                        <AnimatedButton
                                            onClick={() => {
                                                const base = searchQuery.split('.')[0] || searchInput.split('.')[0] || "example";
                                                const domain = `${base}.${item.tld}`;
                                                const isOffer = item.tld === 'com.au';
                                                let finalPrice = price || 12.59;
                                                if (isOffer && finalPrice < 1) finalPrice = 12.59;
                                                const convertedPrice = convertCurrency(finalPrice, selectedCurrency);
                                                handleAddToCart(domain, convertedPrice, isOffer ? 3 : 1);
                                            }}
                                            variant="secondary"
                                            className="w-full py-2.5 rounded-xl border border-white/10 text-xs font-semibold"
                                        >
                                            Add to Cart
                                        </AnimatedButton>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Suggested Domains */}
                {!loading && suggestedResults.length > 0 && (
                    <section className="container" style={{ padding: '0 0 64px' }}>
                        <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#fff', marginBottom: '20px', letterSpacing: '-0.01em' }}>Suggested Domains</h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {suggestedResults.map((result, index) => (
                                <div
                                    key={index}
                                    className="bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300"
                                    style={{ borderRadius: '12px', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', position: 'relative' }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <span style={{ fontWeight: 600, fontSize: '18px', color: '#fff' }}>{result.domain}</span>
                                        {result.domain.toLowerCase().endsWith('.com.au') && (
                                            <span style={{ background: 'linear-gradient(135deg, #ff6b6b, #ff4d4d)', color: '#fff', fontSize: '9px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', letterSpacing: '0.5px' }}>OFFER</span>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexShrink: 0 }}>
                                        {!result.available ? (
                                            <span style={{ fontSize: '12px', fontWeight: 700, padding: '4px 12px', borderRadius: '20px', background: 'rgba(255,80,80,0.15)', color: '#ff6b6b', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                                ALREADY REGISTERED
                                            </span>
                                        ) : (
                                            <>
                                                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '10px', fontWeight: 500 }}>
                                                    {result.domain.toLowerCase().endsWith('.com.au') ? formatPrice((result.registerPrice || 12.59) < 1 ? 12.59 : result.registerPrice, result.domain, true) : formatPrice(result.registerPrice, result.domain)}
                                                    {formatPrice(result.registerPrice, result.domain) !== "Price N/A" && <span style={{ fontSize: '13px', opacity: 0.7, marginLeft: '2px' }}>/{result.domain.toLowerCase().endsWith('.com.au') ? '3yrs' : 'yr'}</span>}
                                                </span>
                                                <AnimatedButton
                                                    onClick={() => {
                                                        const isOffer = result.domain.toLowerCase().endsWith('.com.au');
                                                        let price = result.registerPrice || 12.59;
                                                        if (isOffer && price < 1) price = 12.59;
                                                        const convertedPrice = convertCurrency(price, selectedCurrency);
                                                        handleAddToCart(result.domain, convertedPrice, isOffer ? 3 : 1);
                                                    }}
                                                    variant="secondary"
                                                    className="px-6 py-2 rounded-lg text-sm font-bold flex items-center gap-2"
                                                >
                                                    <ShoppingCart style={{ width: '12px', height: '12px' }} />
                                                    Add
                                                </AnimatedButton>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div style={{ marginTop: '32px', textAlign: 'center' }}>
                            {currentBatchIndex < TLD_BATCHES.length - 1 ? (
                                <div className="mt-8 relative mx-auto text-center group inline-block">
                                    <div className="absolute -inset-4 blur-2xl opacity-0 group-hover:opacity-30 bg-gradient-to-r from-cyan-500 to-blue-600 transition-opacity duration-500 rounded-full"></div>
                                    <div className="relative">
                                        <GradientButton
                                            onClick={() => performSearch(searchQuery, true)}
                                            disabled={isLoadingMore}
                                            className="px-10 py-4 shadow-xl flex items-center gap-3 h-auto"
                                        >
                                            {isLoadingMore ? (
                                                <>
                                                    <Loader2 className="w-5 h-5 animate-spin" />
                                                    Updating...
                                                </>
                                            ) : (
                                                <>
                                                    Load more suggestions
                                                    <ChevronDown style={{ width: '16px', height: '16px', opacity: 0.7 }} />
                                                </>
                                            )}
                                        </GradientButton>
                                    </div>
                                </div>
                            ) : (
                                <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '13px', fontStyle: 'italic' }}>No more suggestions available</p>
                            )}
                        </div>
                    </section>
                )}

                <Footer />
            </div>

            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
                @keyframes shimmer { 100% { transform: translateX(100%); } }
                .btn-spinner { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.2); border-top-color: #fff; border-radius: 50%; animation: spin 0.6s linear infinite; }
                * { box-sizing: border-box; margin: 0; padding: 0; }
                .container { max-width: 1100px; margin: 0 auto; padding-left: 24px; padding-right: 24px; }
                /* Custom scrollbar for dropdown */
                div::-webkit-scrollbar { width: 4px; }
                div::-webkit-scrollbar-track { background: transparent; }
                div::-webkit-scrollbar-thumb { background: rgba(104,130,255,0.3); border-radius: 4px; }
                div::-webkit-scrollbar-thumb:hover { background: rgba(104,130,255,0.5); }
            `}</style>
        </div>
    );
}